import {
  ExternalVendorCodeAutoCompleteQuery,
  useExternalVendorCodeAutoCompleteQuery,
} from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import Select from '@/gf/components/next/Select'
import { Maybe } from '@/types'
import { useMemo, useState } from 'react'

type ExternalVendorCode =
  ExternalVendorCodeAutoCompleteQuery['externalVendorCodesAutocomplete'][number]

// The SAP sync names the row it owns "<vendor name> - <vendor number>", so the
// number is usually already in the name. Only append it when it isn't.
const label = (code: ExternalVendorCode) =>
  code.name.includes(code.externalVendorId) ? code.name : `${code.name} - ${code.externalVendorId}`

/**
 * Picks one of the ERP vendor numbers already synced for the org — for Zachry,
 * the SAP vendor master. Picking from the synced set is what makes a typo
 * impossible; the same rule is enforced server-side (GEA-7397).
 */
const ExternalVendorCodeSelector = ({
  value,
  onChange,
  placeholder,
  autoFocus,
}: {
  value: Maybe<string>
  onChange: (value: Maybe<string>) => void
  placeholder?: string
  autoFocus?: boolean
}) => {
  const client = useGqlClient()
  const [search, setSearch] = useState('')

  const { data } = useExternalVendorCodeAutoCompleteQuery({
    client,
    variables: { search, codes: null },
  })

  const { data: selectedData } = useExternalVendorCodeAutoCompleteQuery({
    client,
    variables: { codes: [value!], search: null },
    skip: !value,
  })

  const options = useMemo(
    () =>
      data?.externalVendorCodesAutocomplete.map((code) => ({
        value: code.externalVendorId,
        label: label(code),
      })),
    [data]
  )

  const selectedOption = useMemo(() => {
    if (!value) return null

    const selected = selectedData?.externalVendorCodesAutocomplete[0]

    return { value, label: selected ? label(selected) : value }
  }, [value, selectedData])

  return (
    <Select
      options={options}
      value={selectedOption}
      onInputChange={setSearch}
      className="text-sm"
      isClearable
      autoFocus={autoFocus}
      placeholder={placeholder}
      onChange={(o) => onChange(o?.value ?? null)}
    />
  )
}

export default ExternalVendorCodeSelector
